/**
 * Bozza automatica del drawer "Nuova anagrafica" in localStorage.
 * Chiave per utente+contesto (lead/cliente): chiudere per sbaglio non perde i dati.
 * Nessuna scrittura sul DB prima del Crea.
 */
import { useCallback, useEffect, useRef } from 'react';

const PREFIX = 'crm-create-client-draft';

function draftKey(userId: string | undefined, isLead: boolean): string {
  return `${PREFIX}:${userId || 'anon'}:${isLead ? 'lead' : 'client'}`;
}

export function useCreateClientDraft(userId: string | undefined, isLead: boolean) {
  const key = draftKey(userId, isLead);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const loadDraft = useCallback((): Record<string, any> | null => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }, [key]);

  const saveDraft = useCallback(
    (formData: Record<string, any>) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        try {
          localStorage.setItem(key, JSON.stringify(formData));
        } catch {
          // localStorage pieno o non disponibile: la bozza è best-effort
        }
      }, 500);
    },
    [key],
  );

  const clearDraft = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return { loadDraft, saveDraft, clearDraft };
}
